import {
  ProjectArticle,
  ProjectGallery,
  ProjectHighlightCards,
  ProjectInlineImage,
  ProjectLearningGrid,
  ProjectNextSteps,
  ProjectProcessTimeline,
  ProjectToolPills,
} from "../ProjectArticle";
import { getProjectSummary } from "../projectIndex";

const project = getProjectSummary("1")!;

function HydroponicsQuotes() {
  const quotes = [
    "The lettuce civilization is entering difficult times.",
    "I had assumed hydration was part of the arrangement.",
    "The nutrients grow thin. The basil have begun discussing survival strategies.",
    "The water has become offensively acidic. The spinach are writing their final letters.",
    "The spinach are experiencing spiritual discomfort.",
    "An inspired new direction in dry hydroponics.",
  ];

  return (
    <section>
      <h2 className="mb-8">Funny Plant Alerts</h2>
      <div className="space-y-8 md:space-y-12">
        {quotes.map((quote) => (
          <blockquote key={quote} className="relative pl-12 md:pl-20">
            <span
              aria-hidden="true"
              className="absolute left-0 -top-5 text-7xl md:text-9xl font-black font-serif text-revelBlue/35 leading-none"
            >
              &ldquo;
            </span>
            <p className="font-serif text-4xl md:text-6xl font-black leading-none text-stem-dark italic">
              {quote}
            </p>
            <div className="mt-4 h-1 w-28 bg-revelBlue" />
          </blockquote>
        ))}
      </div>
    </section>
  );
}

function SystemDiagram() {
  return (
    <section>
      <h2 className="mb-8">How The System Fits Together</h2>
      <div className="mx-auto max-w-5xl overflow-hidden">
        <img
          src="/assets/images/projects/hydroponics-system-infographic.png"
          alt="Diagram showing the hydroponics rack, sensors, Raspberry Pi, Python app, Ollama local LLM, and funny plant alert"
          className="block w-full h-auto"
        />
      </div>
    </section>
  );
}

export default function PersonalityDrivenHydroponicsMonitoringSystem() {
  return (
    <ProjectArticle project={project}>
      <ProjectInlineImage
        src="/assets/images/projects/hydroponics-butler-header.jpg"
        alt="Danial working beside a home hydroponics rack"
        maxWidth="max-w-3xl"
      />

      <p>
        Hydroponics is growing plants without soil. The plants still need the
        important things: water, nutrients, the right pH, good temperature, and
        working pumps. If one of these goes wrong, the plants can start looking
        sad very quickly.
      </p>

      <p>
        So I wanted to make a system that checks those things automatically.
        Checking everything by hand is tedious, and I might forget. Sensors can
        measure the conditions again and again, which is already smart.
      </p>

      <p>
        But an even smarter and funnier idea is to let a local LLM read the
        sensor readings and ramble like a dramatic plant butler. Instead of a
        boring alert, the system can say something like, "The lettuce
        civilization is entering difficult times."
      </p>

      <HydroponicsQuotes />

      <SystemDiagram />

      <p>
        I hooked up sensors to a Raspberry Pi, ran a local LLM on the Raspberry
        Pi using Ollama, and used a small application to push the sensor
        readings into the LLM. The LLM then turns the readings into hilarious
        plant warnings, reminders, and complaints.
      </p>

      <ProjectHighlightCards
        challenge={
          <ul className="space-y-3">
            <li>Making sure the plants get water and nutrients on time.</li>
            <li>Reading sensor values correctly from the hydroponics setup.</li>
            <li>
              Running a local LLM on the Raspberry Pi without making it too
              slow.
            </li>
            <li>Making the jokes funny, but still useful as real alerts.</li>
          </ul>
        }
        result={
          <ul className="space-y-3">
            <li>
              The Raspberry Pi can receive readings from the hydroponics
              sensors.
            </li>
            <li>
              The local LLM can turn those readings into funny warning messages.
            </li>
            <li>The project makes plant care feel less like a chore.</li>
            <li>The system now has a personality instead of just numbers.</li>
          </ul>
        }
      />

      <ProjectProcessTimeline
        items={[
          {
            step: "Understand what plants need",
            detail:
              "Hydroponics plants need water, nutrients, the right pH level, temperature control, and a working pump. These are the things the system should care about.",
          },
          {
            step: "Measure the conditions",
            detail:
              "Sensors check the hydroponics setup periodically so I do not have to keep checking everything manually.",
          },
          {
            step: "Send readings to the Raspberry Pi",
            detail:
              "A small monitoring app collects the sensor readings and prepares them for the AI part of the project.",
          },
          {
            step: "Let the LLM ramble",
            detail:
              "Ollama runs a local LLM on the Raspberry Pi. The app sends it the readings, and the LLM writes funny alerts in the voice of a dramatic hydroponics butler.",
          },
        ]}
      />

      <ProjectLearningGrid
        items={[
          "Plants need regular care even when they are growing without soil.",
          "Sensors are useful because they can check conditions again and again.",
          "A Raspberry Pi can connect the physical world to software.",
          "A local LLM can run without sending everything to the internet.",
          "Funny messages are more memorable than boring warning labels.",
          "AI still needs clear instructions so it does not become nonsense.",
        ]}
      />

      <ProjectToolPills
        tools={[
          "Raspberry Pi",
          "Python",
          "EC sensor",
          "pH sensor",
          "Water level monitoring",
          "Temperature monitoring",
          "Pump status checks",
          "RS-485 / Modbus",
          "Local LLM",
          "Prompt engineering",
          "Hydroponics rack",
        ]}
      />

      <ProjectGallery
        images={[
          {
            src: "/assets/images/projects/hydroponics-butler-header.jpg",
            alt: "Home hydroponics setup with Danial working beside the rack",
          },
          {
            src: "/assets/images/projects/hydroponics-monitoring-system-hero.jpeg",
            alt: "Close-up of plants growing in the hydroponics rack",
          },
          {
            src: "/assets/images/projects/hydroponics-rack.jpeg",
            alt: "Full hydroponics rack with plants under grow lights",
          },
        ]}
      />

      <ProjectNextSteps
        items={[
          {
            title: "Show the hardware",
            detail:
              "Add a fuller gallery with the sensor setup, Raspberry Pi wiring, and plant growth over time.",
          },
          {
            title: "Test more personalities",
            detail:
              "Compare different alert voices to see which one is funniest without becoming distracting.",
          },
          {
            title: "Give the butler memory",
            detail:
              "Let it remember past incidents, grudges, recoveries, and particularly heroic basil moments.",
          },
          {
            title: "Add plant vision",
            detail:
              "Use camera checks or growth analytics so the system can report on visible plant progress.",
          },
        ]}
      />
    </ProjectArticle>
  );
}
